import { getDeviceType, browser } from '@dc/ui-library/src/helpers/browser';
import urljoin from 'url-join';
import createAction from './createAction';
import { viewport, promiseWithTimeout } from '../../utils';
import uiLocale from '../../utils/UILocale';
import * as uiSettings from '../../utils/UISettings';
import { getBasketId } from '../../components/react/PopupStyleAttach/helpers/getBasket';
import { triggerMainProductAddedEvent }
  from '../../components/react/PopupStyleAttach/helpers/triggerMainProductAddedEvent';

// because icepick is implemented using commonjs modules,
// which we don't suppport, it has to be required in this way
const icepick = require('../../../node_modules/icepick/icepick.min');

let deviceType = getDeviceType();

const ADD_TO_BASKET_CALL_TIMEOUT = 60000;

export const PRODUCT_ADDED_TO_BASKET = 'PRODUCT_ADDED_TO_BASKET';
export const BASKET_NOT_COMPATIBLE_WITH_PRODUCT = 'BASKET_NOT_COMPATIBLE_WITH_PRODUCT';
export const CALL_FAILED = 'CALL_FAILED';
export const ADD_TO_BASKET_URL = 'baskets/[BASKET_ID]/products';

// 1. Initial state
const initialState = icepick.freeze({
  device: browser(),
  viewportWidth: viewport().width,
  buttonIdTriggeredAddingToBasketInProgress: null,
  addingToBasketFailedPopup: null,
  selectedCareplanID: 0,
  cartItemsCount: 0,
  productIdForFailedAddToBasket: null,
  storeToken: null,
  storeTokenIsLoading: false

  /*  functionality for services checkboxes postponed
  selectedInstallServiceID: null,
  selectedRecycleServiceID: null
  */
});

// 2. Actions
const UPDATE_DEVICE_TYPE = 'generalAction/UPDATE_DEVICE_TYPE';
const UPDATE_VIEWPORT_WIDTH = 'generalAction/UPDATE_VIEWPORT_WIDTH';
const SET_BUTTON_ID_TRIGGERED_ADDING_TO_BASKET_IN_PROGRESS =
  'generalAction/SET_BUTTON_ID_TRIGGERED_ADDING_TO_BASKET_IN_PROGRESS';
const SET_ADDING_PRODUCT_TO_BASKET_FAILED_POPUP =
  'generalAction/SET_ADDING_PRODUCT_TO_BASKET_FAILED_POPUP';
const SET_PRODUCT_ID_FOR_FAILED_ADD_TO_BASKET =
  'generalAction/SET_PRODUCT_ID_FOR_FAILED_ADD_TO_BASKET';
const DELETE_ADDING_PRODUCT_TO_BASKET_FAILED_POPUP =
  'generalAction/DELETE_ADDING_PRODUCT_TO_BASKET_FAILED_POPUP';
const DELETE_PRODUCT_ID_FOR_FAILED_ADD_TO_BASKET =
  'generalAction/DELETE_PRODUCT_ID_FOR_FAILED_ADD_TO_BASKET';
const SET_CART_ITEMS_COUNT = 'generalAction/SET_CART_ITEMS_COUNT';
const CHANGE_CART_ITEMS_COUNT = 'generalAction/CHANGE_CART_ITEMS_COUNT';
const SET_STORE_TOKEN = 'generalAction/SET_STORE_TOKEN';
const SET_STORE_TOKEN_IS_LOADING = 'generalAction/STORE_TOKEN_IS_LOADING';

/*  functionality for services checkboxes postponed
const SET_SELECTED_INSTALL_SERVICE_ID = 'generalAction/SET_SELECTED_INSTALL_SERVICE_ID';
const DELETE_SELECTED_INSTALL_SERVICE_ID = 'generalAction/DELETE_SELECTED_INSTALL_SERVICE_ID';

const SET_SELECTED_RECYCLE_SERVICE_ID = 'generalAction/SET_SELECTED_RECYCLE_SERVICE_ID';
const DELETE_SELECTED_RECYCLE_SERVICE_ID = 'generalAction/DELETE_SELECTED_RECYCLE_SERVICE_ID';
*/

// 3. Action creators
export function updateDeviceType() {
  return createAction(UPDATE_DEVICE_TYPE)();
}

export function updateViewportWidth() {
  return createAction(UPDATE_VIEWPORT_WIDTH)();
}

export function setButtonIdTriggeredAddingToBasketInProgress(
  buttonIdTriggeredAddingToBasketInProgress
) {
  return createAction(
    SET_BUTTON_ID_TRIGGERED_ADDING_TO_BASKET_IN_PROGRESS
  )(buttonIdTriggeredAddingToBasketInProgress);
}

export function setAddingProductToBasketFailedPopup(popupData) {
  return createAction(SET_ADDING_PRODUCT_TO_BASKET_FAILED_POPUP)(popupData);
}

export function setProductIdForFailedAddToBasket({ productId }) {
  return createAction(SET_PRODUCT_ID_FOR_FAILED_ADD_TO_BASKET)(productId);
}

export function deleteAddingProductToBasketFailedPopup() {
  return createAction(DELETE_ADDING_PRODUCT_TO_BASKET_FAILED_POPUP)(null);
}

export function deleteProductIdForFailedAddToBasket() {
  return createAction(DELETE_PRODUCT_ID_FOR_FAILED_ADD_TO_BASKET)(null);
}

export function setCartItemsCount(cartItemsCount) {
  return createAction(SET_CART_ITEMS_COUNT)(cartItemsCount);
}

export function changeCartItemsCount(relativeCount) {
  return createAction(CHANGE_CART_ITEMS_COUNT)(relativeCount);
}

export function setStoreToken(token) {
  return createAction(SET_STORE_TOKEN)(token);
}

export function setStoreTokenIsLoading(isLoading) {
  return createAction(SET_STORE_TOKEN_IS_LOADING)(isLoading);
}

/*  functionality for services checkboxes postponed
export function setSelectedInstallServiceID(selectedInstallServiceID) {
  return createAction(SET_SELECTED_INSTALL_SERVICE_ID)(selectedInstallServiceID);
}

export function deleteSelectedInstallServiceID() {
  return createAction(DELETE_SELECTED_INSTALL_SERVICE_ID)();
}

export function setSelectedRecycleServiceID(selectedRecycleServiceID) {
  return createAction(SET_SELECTED_RECYCLE_SERVICE_ID)(selectedRecycleServiceID);
}

export function deleteSelectedRecycleServiceID() {
  return createAction(DELETE_SELECTED_RECYCLE_SERVICE_ID)();
}
*/

export const addProductToBasketSilentlyWithPsa = (fupid) => async (dispatch) => {
  const quantity = 1;
  const params = { fupid, quantity };
  let addToBasketResponse;
  try {
    addToBasketResponse = await promiseWithTimeout(
      fetch(
        '/api/cart/addProduct',
        {
          method: 'POST',
          credentials: 'same-origin',
          body: JSON.stringify(params)
        }
      ), ADD_TO_BASKET_CALL_TIMEOUT
    );
  } catch (e) {
    return {
      status: 'failed',
      error: {
        title: 'Network error',
        message: 'AddToBasket endpoint is unreachable.'
      }
    };
  }

  const status = addToBasketResponse.status;
  const responseJSON = await addToBasketResponse.json();

  if (status !== 200 && responseJSON.popupHeaderLabel && responseJSON.popupMessage) {
    return {
      status: 'failed',
      error: {
        title: responseJSON.popupHeaderLabel,
        message: responseJSON.popupMessage
      }
    };

  } if (responseJSON.basketConfirmationUrl && responseJSON.redirectUrl) {
    dispatch(changeCartItemsCount(quantity));
    triggerMainProductAddedEvent(fupid, responseJSON.basketConfirmationUrl);
    return {
      status: 'success',
      fupid: fupid,
      addedQuantity: quantity
    };
  }

  return {
    status: 'failed',
    error: {
      title: 'Unknown error',
      message: 'AddToBasket endpoint return unexpected result.'
    }
  };
};

export const addProductToBasketSilently = (fupid) => (dispatch) => new Promise((resolve) => {
  const quantity = 1;
  const storeUrl = uiSettings.get('global.store_api_url');
  let basketId;
  getBasketId(storeUrl).then((result) => {
    basketId = result;
    const formData = new FormData();
    formData.append('productId', fupid);
    formData.append('onlyStore', 'true');
    const addToBasketUrl = `${storeUrl}/${ADD_TO_BASKET_URL}`
      .replace('[BASKET_ID]', basketId);
    promiseWithTimeout(
      fetch(
        addToBasketUrl,
        {
          method: 'POST',
          credentials: 'include',
          body: formData
        }
      ), ADD_TO_BASKET_CALL_TIMEOUT
    ).then(
      (response) => response.json()
        .then((responseJSON) => ({
          status: response.status,
          responseJSON: responseJSON
        }))
    ).then(({ status, responseJSON }) => {
      if (status !== 200 && responseJSON.error) {
        resolve({
          status: 'failed',
          error: {
            title: responseJSON.error.key,
            message: responseJSON.error.message
          }
        });
      } else if (status === 200 && responseJSON.error === undefined) {
        dispatch(changeCartItemsCount(quantity));
        resolve({
          status: 'success',
          fupid: fupid,
          addedQuantity: quantity
        });
      } else {
        resolve({
          status: 'failed',
          error: {
            title: 'Unknown error',
            message: 'AddToBasket endpoint return unexpected result.'
          }
        });
      }
    })
      .catch((error) => resolve({
        status: 'failed',
        error: {
          title: 'Network error',
          message: 'AddToBasket endpoint is unreachable.'
        }
      }));
  });
});

export const addProductToBasket = (
  {
    fupid, quantity, deliverySlotLabel, deliverySlotPrice, deliverySlotId, deliverySlotPostCode,
    deliverySlotDate, sameDayDelivery, storeId, deliveryType, visitorId, addonProductsIds,
    wasAddedFromWishlist, thisButtonId
  }
) => (dispatch) => {
  const params = {
    fupid: fupid,
    quantity: quantity,
    ...(deliverySlotLabel && { deliverySlotLabel }),
    ...(deliverySlotPrice && { deliverySlotPrice }),
    ...(deliverySlotId && { deliverySlotId }),
    ...(deliverySlotPostCode && { deliverySlotPostCode }),
    ...(deliverySlotDate && { deliverySlotDate }),
    ...(sameDayDelivery && { sameDayDelivery }),
    ...(storeId && { storeId }),
    ...(deliveryType && { deliveryType }),
    ...(visitorId && { visitorId }),
    ...(addonProductsIds && { addonProductsIds }),
    ...(wasAddedFromWishlist && { wasAddedFromWishlist })
  };
  const PSAPopupVisible = window.appState ? window.appState.getState() : {};

  dispatch(setButtonIdTriggeredAddingToBasketInProgress(thisButtonId));

  return new Promise((resolve) => {
    promiseWithTimeout(
      fetch(
        '/api/cart/addProduct',
        {
          method: 'POST',
          credentials: 'same-origin',
          body: JSON.stringify(params)
        }
      ), ADD_TO_BASKET_CALL_TIMEOUT
    ).then(
      (response) => response.json()
        .then((responseJSON) => ({
          status: response.status,
          responseJSON: responseJSON
        }))
    ).then(({ status, responseJSON }) => {
      if (status !== 200 && responseJSON.popupHeaderLabel && responseJSON.popupMessage) {
        // It wasn't possible to add product because
        // the product is incompatible with the current basket content
        dispatch(setAddingProductToBasketFailedPopup(responseJSON));
        dispatch(setProductIdForFailedAddToBasket({ productId: fupid }));
        dispatch(setButtonIdTriggeredAddingToBasketInProgress(null));
        resolve({
          status: BASKET_NOT_COMPATIBLE_WITH_PRODUCT,
          response: responseJSON
        });

      } else if (responseJSON.basketConfirmationUrl && responseJSON.redirectUrl) {
        // Product was succesfully added to basket
        dispatch(setButtonIdTriggeredAddingToBasketInProgress(null));
        dispatch(deleteProductIdForFailedAddToBasket());
        resolve({
          status: PRODUCT_ADDED_TO_BASKET,
          response: responseJSON
        });
      } else {
        throw new Error('An error occured during comunication with Add To Basket AJAX Service');
      }
    }).catch((error) => {
      console.error(error);
      dispatch(setButtonIdTriggeredAddingToBasketInProgress(null));

      // There were error with communication
      if (PSAPopupVisible.productPage.PSAPopupVisible) {
        dispatch(setProductIdForFailedAddToBasket({ productId: fupid }));
      } else {
        dispatch(setProductIdForFailedAddToBasket({ productId: fupid }));
        dispatch(setAddingProductToBasketFailedPopup({
          popupHeaderLabel:
              uiLocale.getString('component.mcd.postcodechecker.error.calling.ajax.popup.header'),
          popupMessage:
              uiLocale.getString('component.mcd.postcodechecker.error.calling.ajax.popup.message'),
          buttonLabel:
              uiLocale.getString('component.mcd.postcodechecker.error.calling.ajax.popup.button')
        }));
      }

      resolve({
        status: CALL_FAILED,
        error: error
      });
    });
  });
};

export const loadStoreToken = () =>
  async (dispatch, getState) => {
    dispatch(setStoreTokenIsLoading(true));
    const storeApiUrl = uiSettings.get('global.store_api_url');
    const state = getState();
    const tokenFromState = state && state.general && state.general.storeToken;
    if (tokenFromState) {
      dispatch(setStoreTokenIsLoading(false));
      return tokenFromState;
    }
    let tokenResponse;
    try {
      tokenResponse = await fetch(
        urljoin(storeApiUrl, '/token'),
        { method: 'POST', credentials: 'include' }
      );
    } catch (e) {
      dispatch(setStoreTokenIsLoading(false));
      return null;
    }
    if (tokenResponse.ok) {
      let token;
      try {
        token = await tokenResponse.json();
      } catch (e) {
        dispatch(setStoreTokenIsLoading(false));
        return null;
      }
      dispatch(setStoreToken(token));
      dispatch(setStoreTokenIsLoading(false));
      return token;
    }
    dispatch(setStoreTokenIsLoading(false));
    return null;
  };

// 4. Reducer
export default function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case UPDATE_DEVICE_TYPE: {

      const newDeviceType = getDeviceType();

      if (deviceType !== newDeviceType) {
        deviceType = newDeviceType;
        return icepick.setIn(state, ['device'], browser());
      }

      return state;
    }
    case UPDATE_VIEWPORT_WIDTH: {
      return icepick.setIn(state, ['viewportWidth'], viewport().width);
    }
    case SET_BUTTON_ID_TRIGGERED_ADDING_TO_BASKET_IN_PROGRESS: {
      return icepick.setIn(state, ['buttonIdTriggeredAddingToBasketInProgress'], action.payload);
    }
    case SET_ADDING_PRODUCT_TO_BASKET_FAILED_POPUP: {
      return icepick.setIn(state, ['addingToBasketFailedPopup'], icepick.freeze(action.payload));
    }
    case SET_PRODUCT_ID_FOR_FAILED_ADD_TO_BASKET: {
      return icepick.setIn(
        state,
        ['productIdForFailedAddToBasket'],
        icepick.freeze(action.payload)
      );
    }
    case DELETE_ADDING_PRODUCT_TO_BASKET_FAILED_POPUP: {
      return icepick.setIn(state, ['addingToBasketFailedPopup'], null);
    }
    case DELETE_PRODUCT_ID_FOR_FAILED_ADD_TO_BASKET: {
      return icepick.setIn(state, ['productIdForFailedAddToBasket'], null);
    }
    case SET_CART_ITEMS_COUNT: {
      return icepick.setIn(state, ['cartItemsCount'], action.payload);
    }
    case CHANGE_CART_ITEMS_COUNT: {
      return icepick.updateIn(state, ['cartItemsCount'], (cartItemsCount) => {
        const newCount = cartItemsCount + parseInt(action.payload, 10);
        if (newCount < 0) {
          // eslint-disable-next-line no-console
          console.log('Warning: Negative cart items count detected. Settting to zero.', newCount);
          return 0;
        }
        return newCount;
      });
    }
    case SET_STORE_TOKEN: {
      return icepick.setIn(state, ['storeToken'], action.payload);
    }
    case SET_STORE_TOKEN_IS_LOADING: {
      return icepick.setIn(state, ['storeTokenIsLoading'], action.payload);
    }
    /*  functionality for services checkboxes postponed
    case SET_SELECTED_INSTALL_SERVICE_ID: {
      return icepick.setIn(state, ['selectedInstallServiceID'], action.payload);
    }
    case DELETE_SELECTED_INSTALL_SERVICE_ID: {
      return icepick.setIn(state, ['selectedInstallServiceID'], null);
    }
    case SET_SELECTED_RECYCLE_SERVICE_ID: {
      return icepick.setIn(state, ['selectedRecycleServiceID'], action.payload);
    }
    case DELETE_SELECTED_RECYCLE_SERVICE_ID: {
      return icepick.setIn(state, ['selectedRecycleServiceID'], null);
    }
    */
    default:
      return state;
  }
}
